import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {LogIn, AlertCircle} from 'lucide-react';
import {useApolloClient} from '@apollo/client/react';
import Api from '../../Api';
import {EnvironmentService} from '../../config/environment';
import cwayLogo from '../../assets/img/logos/cway-logo-gold.svg';

export default function LoginForm() {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [username, setUsername] = useState(localStorage.getItem('last_username') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Set environment to production on mount (fixed in development)
  useEffect(() => {
    EnvironmentService.set('production');
    console.log('[LoginForm] Using production backend via Vite proxy');
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    Api.loginToServer(
      username,
      password,
      (errorMessage: string) => {
        setError(errorMessage || 'Login failed. Please check your credentials.');
        setLoading(false);
      },
      (data: any) => {
        console.log('Login successful', data);

        setLoading(false);

        // Navigate to dashboard - LoginInfo will fetch fresh data
        navigate('/admin/dashboard', {replace: true});
      },
    );
  };

  return (
    <div className="max-w-md w-full space-y-8">
      {/* Logo and Title */}
      <div className="text-center">
        <div className="mx-auto w-48 h-20 flex items-center justify-center">
          <img src={cwayLogo} alt="Cway Logo" className="w-full h-full object-contain" />
        </div>
        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Cway Admin</h2>
        <p className="mt-2 text-sm text-gray-600">Sign in to access the admin dashboard</p>
      </div>

      {/* Login Form */}
      <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0" size={20} />
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Username Field */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
              placeholder="Enter your username"
              disabled={loading}
            />
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
              placeholder="Enter your password"
              disabled={loading}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Signing in...
              </>
            ) : (
              <>
                <LogIn size={20} />
                Sign in
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
